import { z } from 'zod';
export const DashboardKpiSchema = z.object({
    billingThisMonthCents: z.number().int(),
    billingThisYearCents: z.number().int(),
    paidThisYearCents: z.number().int(),
    pendingTotalCents: z.number().int(),
    overduePendingCents: z.number().int(),
    activeOpportunitiesCount: z.number().int(),
    openTasksCount: z.number().int(),
    myOpenTasksCount: z.number().int(),
    unbilledTimeMinutes: z.number().int(),
    unbilledTimeAmountCents: z.number().int(),
    fundsAvailableTotalCents: z.number().int(),
});
export const MonthlyBillingPointSchema = z.object({
    /** YYYY-MM */
    month: z.string(),
    totalCents: z.number().int(),
    paidCents: z.number().int(),
    invoicesCount: z.number().int(),
});
export const MonthlyBillingResponseSchema = z.object({
    points: z.array(MonthlyBillingPointSchema),
});
export const BreakdownPointSchema = z.object({
    key: z.string(),
    label: z.string(),
    count: z.number().int(),
});
export const OpportunitiesBreakdownResponseSchema = z.object({
    byStage: z.array(BreakdownPointSchema),
    byServiceLine: z.array(BreakdownPointSchema),
});
export const TopClientItemSchema = z.object({
    clientId: z.string(),
    clientDisplayName: z.string(),
    totalCents: z.number().int(),
    paidCents: z.number().int(),
    invoicesCount: z.number().int(),
});
export const TopClientsResponseSchema = z.object({
    year: z.number().int(),
    items: z.array(TopClientItemSchema),
});
// Forecast de facturación a fin de año: combina facturación real, hitos
// planificados, ingresos recurrentes proyectados y pipeline ponderado.
export const ForecastMonthSchema = z.object({
    /** YYYY-MM */
    month: z.string(),
    /** Facturación real (Invoice EMITIDA/COBRADA/PARCIAL) del mes. */
    invoicedCents: z.number().int(),
    /** Hitos de pago PENDIENTE con dueDate en el mes. */
    milestonesCents: z.number().int(),
    /** Suma del pipeline ponderado por probabilidad de cierre. */
    pipelineExpectedCents: z.number().int(),
    /** Recurrencias activas proyectadas al mes. */
    recurringCents: z.number().int(),
    /** Suma de los 4 componentes anteriores. */
    totalExpectedCents: z.number().int(),
});
export const ForecastResponseSchema = z.object({
    year: z.number().int(),
    /** 12 meses (1-12) en orden cronológico. */
    months: z.array(ForecastMonthSchema),
    totalYearCents: z.number().int(),
});
// ---------------------------------------------------------------------------
// Dashboard overview (rediseño): agregados por área para un periodo
// (año / trimestre / mes). Un solo endpoint alimenta las 4 áreas.
// ---------------------------------------------------------------------------
/** Punto de desglose con nº de elementos + importe asociado (céntimos). */
export const AmountBreakdownPointSchema = z.object({
    key: z.string(),
    label: z.string(),
    count: z.number().int(),
    amountCents: z.number().int(),
});
/** Serie mensual con dos magnitudes (p.ej. puntual vs recurrente). */
export const MonthSeriesPointSchema = z.object({
    /** YYYY-MM */
    month: z.string(),
    primaryCents: z.number().int(),
    secondaryCents: z.number().int(),
});
/** Finanzas por proyecto: vendido / facturado / cobrado. */
export const ProjectFinancePointSchema = z.object({
    opportunityId: z.string(),
    label: z.string(),
    soldCents: z.number().int(),
    billedCents: z.number().int(),
    collectedCents: z.number().int(),
});
/** Venta por advisor: importe vendido + comisión. */
export const AdvisorSalesPointSchema = z.object({
    key: z.string(),
    label: z.string(),
    soldCents: z.number().int(),
    commissionCents: z.number().int(),
});
/** Tarea abierta resumida para el listado del panel. */
export const DashboardOpenTaskSchema = z.object({
    id: z.string(),
    title: z.string(),
    dueAt: z.string().datetime().nullable(),
    priority: z.string(),
});
export const DashboardOverviewResponseSchema = z.object({
    period: z.object({
        year: z.number().int(),
        quarter: z.number().int().nullable(),
        month: z.number().int().nullable(),
        label: z.string(),
    }),
    opportunities: z.object({
        newCount: z.number().int(),
        sentCount: z.number().int(),
        lostCount: z.number().int(),
        wonCount: z.number().int(),
        byStage: z.array(AmountBreakdownPointSchema),
        byServiceLine: z.array(AmountBreakdownPointSchema),
        byAdvisor: z.array(AmountBreakdownPointSchema),
    }),
    projects: z.object({
        onboardingCount: z.number().int(),
        executionCount: z.number().int(),
        supportCount: z.number().int(),
        finishedCount: z.number().int(),
        byPhase: z.array(AmountBreakdownPointSchema),
        byRoadmap: z.array(AmountBreakdownPointSchema),
        byMilestone: z.array(AmountBreakdownPointSchema),
        finance: z.array(ProjectFinancePointSchema),
    }),
    economics: z.object({
        billedCents: z.number().int(),
        spentCents: z.number().int(),
        /** Parte de spentCents que proviene de oportunidades En Inversión. */
        investedCents: z.number().int(),
        pendingIssueCents: z.number().int(),
        pendingCollectCents: z.number().int(),
        billingByServiceLine: z.array(AmountBreakdownPointSchema),
        billingByClient: z.array(AmountBreakdownPointSchema),
        expenseByCategory: z.array(AmountBreakdownPointSchema),
        /** 12 meses del año: primary = puntual (hitos), secondary = recurrente. */
        salesByMonth: z.array(MonthSeriesPointSchema),
        /** Venta por advisor (oportunidades ganadas del periodo): vendido + comisión. */
        advisorSales: z.array(AdvisorSalesPointSchema),
        topClients: z.array(TopClientItemSchema),
    }),
    activities: z.object({
        openTasksCount: z.number().int(),
        myOpenTasksCount: z.number().int(),
        upcomingEventsCount: z.number().int(),
        /** Listado corto de tareas abiertas (para el panel). */
        openTasks: z.array(DashboardOpenTaskSchema),
    }),
});
//# sourceMappingURL=stats.js.map